import logger from 'logger';

import { getAuthClient } from './clients';

type AuthUser = {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string | null;
  initials: string;
  creditInCents: number;
};

/**
 * Authenticates a user via a Google ID token. Rails verifies the token against
 * Google, then either signs in an existing (email, tenant_id) match or
 * creates a new account through `Checkout::RegisterUser`.
 *
 * The endpoint returns 401 with a generic auth_failure for any verification
 * problem (missing/expired/malformed/forged token, incomplete Google
 * payload) so callers can't distinguish *why* it failed.
 */
export async function oauth(input: {
  oauthToken: string;
  referralCode?: string;
  signInLocation?: string;
}): Promise<{ ok: true; user: AuthUser } | { ok: false }> {
  const client = getAuthClient();

  let res;
  try {
    res = await client.post<{
      success: boolean;
      data: AuthUser;
      error: {
        code: number | null;
        message: string | null;
        validation: unknown;
      };
    }>('/oauth', {
      oauth_token: input.oauthToken,
      referral_code: input.referralCode,
      sign_in_location: input.signInLocation,
    });
  } catch (err: any) {
    logger.error({
      msg: 'OauthRequestFailed',
      noticable: err,
      extra: { context: { status: err?.response?.status } },
    });
    return { ok: false };
  }

  if (!res.data?.success || !res.data?.data) {
    logger.error({
      msg: 'OauthNonOkResponse',
      noticable: new Error('oauth response did not include user data'),
    });
    return { ok: false };
  }

  return { ok: true, user: res.data.data };
}
